"use client";

import { useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import BreadcrumbWidget from "@/components/Widgets/BreadcrumbWidget";
import SeoHead from "@/components/SEO/SeoHead";

type Course = {
  title: string;
  slug: string;
  description: string;
  duration: string;
  level: string;
};

type CourseCategory = {
  title: string;
  description: string;
  courses: Course[];
};

type Props = {
  courseCategories: CourseCategory[];
};

type FilterKey = "all" | "data-bi" | "healthcare" | "programming" | "cloud-erp" | "it-infrastructure" | "ai-ml" | "generative-ai" | "automation-rpa" | "cloud-erp-tech";

const FILTER_TABS: Array<{ key: FilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "data-bi", label: "Data & BI" },
  { key: "healthcare", label: "Healthcare" },
  { key: "programming", label: "Programming" },
  { key: "cloud-erp", label: "Cloud/ERP" },
  { key: "it-infrastructure", label: "IT Infrastructure" },
  { key: "ai-ml", label: "AI & Machine Learning" },
  { key: "generative-ai", label: "Generative AI" },
  { key: "automation-rpa", label: "Automation & RPA" },
  { key: "cloud-erp-tech", label: "Cloud & ERP Technologies" },
];

const CATEGORY_TO_TITLE: Record<Exclude<FilterKey, "all">, string> = {
  "data-bi": "Data & Business Intelligence",
  healthcare: "Healthcare & Analytics",
  programming: "Programming & Development",
  "cloud-erp": "Cloud/ERP",
  "it-infrastructure": "IT Infrastructure & Management",
  "ai-ml": "AI & Machine Learning",
  "generative-ai": "Generative AI & Prompt Engineering",
  "automation-rpa": "Automation & Robotic Process Automation",
  "cloud-erp-tech": "Cloud & ERP Technologies",
};

const CATEGORY_SEO: Record<FilterKey, { title: string; description: string }> = {
  all: {
    title: "IT Training Courses Online | Seasoft Classes - 300+ Programs",
    description:
      "Browse 300+ online IT courses in Data Warehousing, SAS, Power BI, Java, Salesforce, QA, Cybersecurity, PMP, Selenium and more. Taught by certified industry experts. Enroll now.",
  },
  "data-bi": {
    title: "Data & BI Courses | Seasoft Classes",
    description:
      "Explore Data & BI courses from Seasoft Classes, including Power BI, Tableau, Data Warehousing, MicroStrategy, and Business Objects training.",
  },
  healthcare: {
    title: "Healthcare Courses | Seasoft Classes",
    description:
      "Discover healthcare and analytics training in SAS, Pharmacovigilance, Drug Safety, and Clinical Data Management with Seasoft Classes.",
  },
  programming: {
    title: "Programming Courses | Seasoft Classes",
    description:
      "Build software and automation skills with Seasoft Classes programming courses in Java, Python, Selenium, and Full Stack Development.",
  },
  "cloud-erp": {
    title: "Cloud/ERP Courses | Seasoft Classes",
    description:
      "Get job-ready with Seasoft Classes Cloud/ERP training in Salesforce, NetSuite, SAP FICO, and ServiceNow for enterprise platforms.",
  },
  "it-infrastructure": {
    title: "IT Infrastructure Courses | Seasoft Classes",
    description:
      "Prepare for infrastructure and management roles with Seasoft Classes training in CCNA, Linux, Cybersecurity, QA Testing, PMP, and Agile.",
  },
  "ai-ml": {
    title: "AI & Machine Learning Courses | Seasoft Classes",
    description:
      "Master Artificial Intelligence and Machine Learning with Seasoft Classes training in Deep Learning, Neural Networks, TensorFlow, and AI development.",
  },
  "generative-ai": {
    title: "Generative AI & Prompt Engineering Courses | Seasoft Classes",
    description:
      "Learn Generative AI, Large Language Models, ChatGPT, Claude, Prompt Engineering, and AI automation with Seasoft Classes expert training.",
  },
  "automation-rpa": {
    title: "Automation & RPA Courses | Seasoft Classes",
    description:
      "Automate business processes with Seasoft Classes training in RPA, Blue Prism, UiPath, and workflow automation for enterprise efficiency.",
  },
  "cloud-erp-tech": {
    title: "Cloud & ERP Technologies Courses | Seasoft Classes",
    description:
      "Master Oracle Cloud technologies with Seasoft Classes training in ERP, HCM, SCM, EPM, CX, and Analytics.",
  },
};

function getImageByIndex(index: number) {
  const images = [
    "/images/skill-1.jpg",
    "/images/skill-2.jpg",
    "/images/skill-3.jpg",
    "/images/skill-4.jpg",
  ];

  return images[index % images.length];
}

function getCategoryKeyFromQuery(value: string | null): FilterKey {
  if (!value) {
    return "all";
  }

  return FILTER_TABS.some((tab) => tab.key === value) ? (value as FilterKey) : "all";
}

export default function CoursesIndexClient({ courseCategories }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const selectedCategory = getCategoryKeyFromQuery(searchParams?.get("category") || null);
  const seo = CATEGORY_SEO[selectedCategory];

  const normalizedCategories = useMemo(() => {
    return courseCategories.map((category) => ({
      ...category,
      courses: category.courses.map((course, courseIndex) => ({
        ...course,
        image: getImageByIndex(courseIndex),
      })),
    }));
  }, [courseCategories]);

  const displayedCategories = useMemo(() => {
    if (selectedCategory === "all") {
      return normalizedCategories;
    }

    const titleToMatch = CATEGORY_TO_TITLE[selectedCategory];
    return normalizedCategories.filter((category) => category.title === titleToMatch);
  }, [normalizedCategories, selectedCategory]);

  const preloadImages = useMemo(() => {
    if (selectedCategory !== "all") {
      return [];
    }

    return normalizedCategories
      .flatMap((category) => category.courses)
      .slice(0, 6)
      .map((course) => course.image);
  }, [normalizedCategories, selectedCategory]);

  const handleTabClick = (key: FilterKey) => {
    const params = new URLSearchParams(searchParams?.toString() || "");

    if (key === "all") {
      params.delete("category");
    } else {
      params.set("category", key);
    }

    const query = params.toString();
    const nextUrl = query ? `/courses?${query}` : "/courses";
    router.replace(nextUrl, { scroll: false });
  };

  return (
    <main className="bg-white">
      <SeoHead
        currentPath="/courses"
        title={seo.title}
        description={seo.description}
        canonical="https://seasoftclasses.com/courses"
        preloadImages={preloadImages}
      />

      <BreadcrumbWidget
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Courses", href: "" },
        ]}
      />

      <section className="bg-[#002147] py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <h1 className="text-white text-3xl md:text-5xl font-black leading-tight max-w-4xl">
            Explore 300+ Online IT Training Courses
          </h1>
          <p className="text-blue-100 text-base md:text-lg mt-6 max-w-4xl leading-relaxed">
            From beginner to advanced, find the right course to launch or accelerate your IT career, taught by certified experts with real-world consulting experience.
          </p>
        </div>
      </section>

      <section className="py-8 md:py-10 border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {FILTER_TABS.map((tab) => {
              const active = tab.key === selectedCategory;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleTabClick(tab.key)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-colors cursor-pointer ${
                    active
                      ? "bg-[#1B61FF] text-white"
                      : "bg-[#F3F4F6] text-[#1A1A1A] hover:bg-[#E5E7EB]"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 space-y-14 md:space-y-20">
          {displayedCategories.map((category) => (
            <div key={category.title} className="space-y-8">
              <div className="border-l-4 border-blue-600 pl-5">
                <h2 className="text-2xl md:text-4xl font-black text-[#002147]">{category.title}</h2>
                <p className="text-gray-600 mt-4 text-base md:text-lg leading-relaxed max-w-5xl">
                  {category.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
                {category.courses.map((course) => (
                  <article
                    key={course.title}
                    className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="relative h-[190px] w-full">
                      <Image
                        src={course.image}
                        alt={`${course.title} course at Seasoft Classes`}
                        fill
                        loading="lazy"
                        className="object-cover"
                      />
                    </div>

                    <div className="p-6 md:p-7">
                      <h3 className="text-[#002147] text-xl font-extrabold leading-snug">
                        <Link prefetch={true} href={course.slug} className="hover:text-[#1B61FF] transition-colors">
                          {course.title}
                        </Link>
                      </h3>
                      <p className="text-gray-600 mt-4 text-sm leading-relaxed">{course.description}</p>

                      <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between gap-4">
                        <span className="inline-block bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1.5 rounded-full">
                          Duration: {course.duration}
                        </span>
                        <span className="inline-block bg-gray-100 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-full">
                          Level: {course.level}
                        </span>
                      </div>

                      <div className="mt-5">
                        <Link
                          prefetch={true}
                          href={course.slug}
                          className="inline-flex items-center text-sm font-bold text-[#1B61FF] hover:text-blue-700 transition-colors"
                        >
                          View Course
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
